import { ArrowRight, Calendar, User, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { blogs } from "@/lib/blogData";

// Simulating CRM Blog Connection
const fetchBlogs = async () => {
  await new Promise(resolve => setTimeout(resolve, 800));
  return blogs;
};

import { SEOHead } from "@/components/SEOHead";
import { CardSkeleton } from "@/components/SkeletonLoaders";

const Blog = () => {
  const { data: posts, isLoading } = useQuery({
    queryKey: ["crm-blogs"],
    queryFn: fetchBlogs,
  });
  return (
    <div className="min-h-screen bg-background pt-32 pb-24">
      <SEOHead
        title="Insights & Strategies"
        description="Actionable advice on web design, automation, and scaling your business. Synced directly from our CRM."
        canonical="/blog"
      />
      <div className="container mx-auto px-6 max-w-5xl">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Insights & <span className="text-primary">Strategies</span>
          </h1>
          <p className="text-xl text-muted-foreground animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150 fill-mode-both">
            Actionable advice on web design, automation, and scaling your business. Synced directly from our CRM.
          </p>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts?.map((post, i) => (
              <Card key={i} className="bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 group hover:-translate-y-2 hover:shadow-[0_0_30px_hsl(var(--primary)/0.15)] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-8 fill-mode-both" style={{ animationDelay: `${300 + i * 150}ms` }}>
                <Link to={`/blog/${post.slug}`} className="block aspect-video overflow-hidden border-b border-border/50 relative">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-60" />
                </Link>
                <CardHeader>
                  <div className="text-xs font-bold text-primary mb-2 uppercase tracking-wider">{post.category}</div>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors leading-tight">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </CardTitle>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-4">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
                    <span className="flex items-center gap-1"><User className="w-3 h-3" /> {post.author}</span>
                  </div>
                </CardHeader>
                <CardContent className="text-muted-foreground flex-grow flex flex-col justify-between">
                  <p className="mb-6">{post.excerpt}</p>
                  <Link to={`/blog/${post.slug}`} className="text-primary text-sm font-bold hover:underline inline-flex items-center mt-auto">
                    Read Article <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;